import type {
  CATAssignedWorkspace,
  CATAssignedWorkspacePage,
  CATInvitationState,
  CATWorkflowAssignment,
} from "@/entities/translation/translation.types"

/**
 * 초대가 아직 협업자로 만들어 주지 않은 상태인지.
 *
 * `accepted`와 비교하지 않고 미수락 쪽을 나열한다. 서버 마이그레이션이 기존 행에 `'accepted'`를
 * 기본값으로 넣었듯이, 상태를 모르는 행은 협업자로 본다 — 백엔드보다 콘솔이 먼저 배포돼 필드가
 * 아직 안 내려오는 동안 멀쩡한 담당자를 전부 차단하지 않기 위해서다.
 */
function isBlockedByInvitation(
  invitationState: CATInvitationState
): invitationState is "pending" | "declined" {
  return invitationState === "pending" || invitationState === "declined"
}

/**
 * 내비게이션 항목이 켜져야 하는지.
 *
 * `total > 0`으로는 안 된다. 목록은 거절한 행도 남겨 두므로 — 거절은 행을 지우는 게 아니라 상태다 —
 * 유일한 초대를 거절한 사람에게 할 일 없는 페이지를 가리키는 항목이 영원히 남는다.
 *
 * 그렇다고 "답하지 않은 초대"만 세도 안 된다. 수락하는 순간 그 수는 0이 되는데, 초대받은 사람에게는
 * 이 항목이 그 작업으로 가는 **유일한** 경로다 — 남의 작업이라 자기 파일 목록에 없다. 수락하자마자
 * 방금 맡은 일로 돌아갈 길이 사라진다.
 *
 * 그래서 행 상태로 판정한다. 거절이 아닌 행이 하나라도 있으면 켠다. 집계 필드가 아니라 행을 보는
 * 이유는 그것이 지금 배포된 백엔드(1.85.0)가 실제로 내려주는 것이기 때문이다.
 *
 * 다 못 본 경우에는 켜는 쪽으로 기운다. 가져온 행이 전부 거절이어도 `total`이 그보다 크면 못 본 행에
 * 일이 있을 수 있다. 할 일 없는 항목이 남는 것보다 맡은 일로 못 돌아가는 쪽이 나쁘다.
 */
export function hasActionableCATAssignments(
  page: Pick<CATAssignedWorkspacePage, "items" | "total">
): boolean {
  const items = page.items ?? []
  if (items.some((item) => item.invitationState !== "declined")) return true
  return page.total > items.length
}

/**
 * 할당 카드가 보여야 하는 상태.
 *
 * 서버는 초대 상태와 완료 시각을 별개로 내려주지만 카드에서는 한 번에 하나만 의미가 있다. 답하지
 * 않은 초대는 완료 여부보다 먼저 보여야 하고 — 답하기 전에는 작업 자체를 열 수 없다 — 거절한 뒤에
 * 남은 행에 "진행 중" 배지를 붙이면 열 수 없는 작업을 열 수 있다고 말하는 셈이 된다.
 */
export type CATAssignedWorkspaceState =
  | "pending"
  | "declined"
  | "completed"
  | "active"

export function getCATAssignedWorkspaceState({
  invitationState,
  completedAt,
}: Pick<
  CATAssignedWorkspace,
  "invitationState" | "completedAt"
>): CATAssignedWorkspaceState {
  if (isBlockedByInvitation(invitationState)) return invitationState
  return completedAt ? "completed" : "active"
}

/**
 * 편집기 링크를 걸어도 되는지.
 *
 * 수락한 사람만 워크스페이스를 연다. 서버가 `resolve_access`에서 `invitation_state = 'accepted'`로
 * 거르므로 링크를 남겨 두면 눌렀을 때 권한 오류만 나고 이유는 어디에도 안 나온다.
 *
 * 완료 여부는 보지 않는다. 끝난 작업도 수락한 사람은 읽기용으로 다시 열 수 있고, 그 판단은
 * 워크스페이스 안에서 `phase`가 내린다.
 */
export function canOpenCATAssignedWorkspace(
  workspace: Pick<CATAssignedWorkspace, "invitationState">
): boolean {
  return !isBlockedByInvitation(workspace.invitationState)
}

/**
 * 멘션 후보인지.
 *
 * 서버는 수락한 협업자만 멘션으로 받고 그 외에는 댓글 전체를 400으로 거절한다. 후보 목록을 거르지
 * 않으면 사용자는 댓글을 다 쓰고 나서야 실패를 본다.
 */
export function isCATMentionCandidate(
  assignment: Pick<CATWorkflowAssignment, "accountId" | "invitationState">,
  currentUserId?: string
): boolean {
  if (isBlockedByInvitation(assignment.invitationState)) return false
  return assignment.accountId !== currentUserId
}

/**
 * 이미 고른 멘션 중 지금도 후보인 것만.
 *
 * 후보 목록은 5초마다 갱신되는 워크플로에서 파생되므로, 작성 도중에 상대가 초대를 거절하면 칩은
 * 화면에서 사라지는데 선택 상태에는 그대로 남는다. 그대로 보내면 서버가 댓글 전체를 400으로
 * 거절하고, 사용자는 무엇을 지워야 하는지 볼 수도 없다 — 지울 칩이 이미 없기 때문이다.
 */
export function selectableCATMentionIds(
  selected: readonly string[],
  candidates: readonly Pick<CATWorkflowAssignment, "accountId">[]
): string[] {
  const available = new Set(candidates.map((candidate) => candidate.accountId))
  return selected.filter((accountId) => available.has(accountId))
}

/**
 * 답한 작업 한 건의 상태를 이미 불러온 페이지들에 반영한다.
 *
 * 재조회를 기다리지 않고 먼저 덧칠하는 이유: POST 는 성공했는데 뒤따르는 목록 재조회가 실패하면
 * 무효화는 이전 데이터를 그대로 두므로 카드가 계속 "수락 대기"로 남고 버튼도 다시 살아난다. 그때는
 * 뮤테이션이 성공했으니 오류 토스트도 뜨지 않아, 답을 이미 보낸 사람이 답하지 않은 화면을 본다.
 *
 * 다른 필드는 건드리지 않는다. 서버 응답이 담고 있는 것은 초대 한 건이지 작업 전체가 아니다.
 */
export function applyCATInvitationAnswer(
  pages: readonly CATAssignedWorkspacePage[],
  jobId: string,
  invitationState: CATInvitationState
): CATAssignedWorkspacePage[] {
  return pages.map((page) => ({
    ...page,
    items: page.items.map((item) =>
      item.jobId === jobId ? { ...item, invitationState } : item
    ),
  }))
}
